import { useEffect } from "react";
import { FIELD_SECTIONS, LIST_SECTIONS, type SectionName } from "@npac/rules";
import { locationView, sectionView } from "@core/status";
import { formatTime } from "../i18n";
import type { Strings } from "../i18n";
import { useApp, useQuery, useSession } from "../ui/context";
import { href } from "../ui/router";
import { Banner, LinkButton, StatusPill } from "../ui/components";

export function BoothScreen({ code }: { code: string }) {
  const { app, t, lang } = useApp();
  const { assemblyId } = useSession();

  // fresh section data in the background; offline keeps the cache
  useEffect(() => {
    void app.core.loadBooth(code);
  }, [app, code]);

  const data = useQuery(
    async () => {
      if (!assemblyId) return null;
      const [booth, outbox, drafts, server] = await Promise.all([
        app.core.store.getBooth(assemblyId, code),
        app.core.store.outboxForBooth(assemblyId, code),
        app.core.store.draftSections(assemblyId, code),
        app.core.store.getServerSections(assemblyId, code),
      ]);
      return { booth, outbox, drafts, server };
    },
    [assemblyId, code],
    ["booths", "sections", "outbox", "drafts"]
  );

  if (!data) return null;
  const { booth, outbox, drafts, server } = data;
  if (!booth) return <Banner kind="error">{t.boothUnknown(code)}</Banner>;
  const editable = app.canEdit(booth);
  const loc = locationView(booth, outbox);
  const can = (x: Parameters<typeof app.can>[0]) => app.can(x);
  const anyBoothWork = app.screens.some((x) => x !== "rallies" && x !== "tour" && x !== "vehicles" && x !== "voted" && x !== "counting");

  return (
    <div data-testid="booth-screen">
      <div style={{ fontWeight: 800, color: "var(--primary)" }}>
        {t.boothNo} {booth.code}
      </div>
      <h2 style={{ marginTop: 0 }}>{booth.name}</h2>
      <p style={{ marginTop: -6 }}>
        {t.mandal}: {booth.mandal || t.emptyValue} • {t.panchayat}: {booth.panchayat || t.emptyValue}
        <br />
        {t.village}: {booth.village || t.emptyValue}
      </p>

      {!editable ? (
        <Banner kind="warn" testId="booth-not-assigned">
          {t.notAllowedEdit}
        </Banner>
      ) : null}

      {!anyBoothWork ? <Banner kind="info">{t.noBoothDuty}</Banner> : null}

      {editable && can("location") ? (
        <div style={{ marginBottom: 16 }}>
          <LinkButton
            href={href({ name: "location", code })}
            variant="primary"
            big
            testId="share-location"
            sub={
              loc === "shared" && booth.location
                ? t.locationOnServer(booth.location.accuracyM != null ? String(Math.round(booth.location.accuracyM)) : "?", formatTime(booth.location.sharedAt, lang))
                : loc === "waiting"
                  ? t.locationWaiting
                  : loc === "failed"
                    ? t.locationFailed
                    : t.shareLocationSub
            }
          >
            📍 {t.shareLocation}
          </LinkButton>
        </div>
      ) : null}

      {FIELD_SECTIONS.filter((s) => can(s)).map((s) => {
        const v = sectionView(booth, s, outbox, drafts);
        const srv = server[s];
        const issues = v.item ? v.item.payload.issues : srv?.issues ?? [];
        return (
          <a key={s} href={href({ name: "section", code, section: s })} className={`card card-link st-${v.status}`} data-testid={`section-${s}`}>
            <div className="card-head">
              <h3>{t[`sec_${s}` as const]}</h3>
              <span className="chev" aria-hidden>
                ›
              </span>
            </div>
            <StatusPill status={v.status} t={t} />
            {v.status === "waiting" && v.localStatus ? <div className="muted">→ {t[`st_${v.localStatus}` as const]}</div> : null}
            {issues.slice(0, 2).map((i) => (
              <div key={i.code} style={{ fontSize: 15, marginTop: 4 }}>
                • {lang === "hi" ? i.hi : i.en}
              </div>
            ))}
            {srv?.counts ? <div className="ao-sum">{countLine(s, srv.counts, t)}</div> : null}
            {v.hasDraft ? <div className="view-only">✎ {t.draftSaved}</div> : null}
            {srv?.updatedBy && !v.item ? <div className="muted">{t.lastBy(`${srv.updatedBy}, ${formatTime(srv.updatedAt, lang)}`)}</div> : null}
          </a>
        );
      })}

      {can("sentiment") ? <ExtraCard code={code} to={href({ name: "sentiment", code })} title={t.secSentiment} section="sentiment" testId="section-sentiment" /> : null}
      {LIST_SECTIONS.filter((l) => can(l)).map((l) => (
        <ExtraCard key={l} code={code} to={href({ name: "people", code, list: l })} title={t[`list_${l}` as const]} section={l} testId={`section-${l}`} />
      ))}
      {editable && anyBoothWork ? (
        <a href={href({ name: "places", code })} className="card card-link" data-testid="section-places">
          <div className="card-head">
            <h3>🏘 {t.bsTitle}</h3>
            <span className="chev" aria-hidden>
              ›
            </span>
          </div>
          <div className="muted">{t.bsCardSub}</div>
        </a>
      ) : null}
      {can("result") ? (
        <a href={href({ name: "result", code })} className="card card-link" data-testid="section-result">
          <div className="card-head">
            <h3>🗳 {t.secResult}</h3>
            <span className="chev" aria-hidden>
              ›
            </span>
          </div>
          <div className="muted">{t.resNeedsNet}</div>
        </a>
      ) : null}
    </div>
  );
}

/** Card for the survey and the open name lists: status, and for a list how many names it has. */
function ExtraCard({ code, to, title, section, testId }: { code: string; to: string; title: string; section: SectionName; testId: string }) {
  const { app, t } = useApp();
  const { assemblyId } = useSession();
  const d = useQuery(
    async () => {
      if (!assemblyId) return null;
      const [booth, item, server] = await Promise.all([
        app.core.store.getBooth(assemblyId, code),
        app.core.store.findSectionItem(assemblyId, code, section),
        app.core.store.getServerSection(assemblyId, code, section),
      ]);
      return { booth, item, server };
    },
    [assemblyId, code, section],
    ["booths", "sections", "outbox"]
  );
  const v = sectionView(d?.booth ?? null, section, d?.item ? [d.item] : []);
  // counts only: names on the server + names typed on this phone and not sent yet
  const onServer = d?.server?.counts?.people ?? d?.booth?.sections?.[section]?.count ?? 0;
  const pend = (d?.item?.payload.data as { people?: unknown[] } | undefined)?.people;
  const waiting = Array.isArray(pend) ? pend.length : 0;
  return (
    <a href={to} className={`card card-link st-${v.status}`} data-testid={testId}>
      <div className="card-head">
        <h3>{title}</h3>
        <span className="chev" aria-hidden>
          ›
        </span>
      </div>
      <StatusPill status={v.status} t={t} />
      {section !== "sentiment" ? (
        <div className="ao-sum">
          {t.aoNamesCount(onServer)}
          {waiting ? ` · ${t.aoPending(waiting)}` : ""}
        </div>
      ) : null}
    </a>
  );
}

/** One line of counts for a section card (the phone never gets the values). */
function countLine(section: SectionName, c: Record<string, number>, t: Strings): string {
  const of = (keys: string[]) => `${keys.filter((k) => c[k]).length}/${keys.length} ${t.aoFilledShort}`;
  switch (section) {
    case "identity":
      return of(["mandal", "panchayat", "village", "landmark"]);
    case "agents":
      return of(["pollingAgent", "coPollingAgent", "bla2", "blo"]);
    case "karyakarta":
      return `${c.people ?? 0}/20 ${t.aoMembersLabel}`;
    case "demographics":
      return c.totalVoters ? `${t.aoFilled} · ${t.aoCastes(c.castes ?? 0)}` : t.aoEmpty;
    case "ground":
      return `${t.aoProblems} ${c.problems ?? 0} · ${t.aoSuggestions} ${c.suggestions ?? 0}`;
    default:
      return "";
  }
}
